<template>
    <LayoutPage v-if="footer.uuid">
        <template #header>
            <LegalProjectHeader
                :actions="actions"
                :shareLinks="shareLinks"
                @share="getNdas"
                @publish="handlePublish"
            >
                <div v-if="ndas.length" class="w-full flex items-center text-left space-x-3">
                    <CardIcon>
                        <TextFileIcon />
                    </CardIcon>
                    <p>NDA</p>
                    <FormSelect
                        class="flex-grow"
                        :options="ndaList"
                        :value="selectedNda"
                        @change="selectedNda = $event"
                    />
                    <div class="space-x-2">
                        <ButtonSecondaryIconMd
                            :disabled="!selectedNda"
                            @click="generateNda"
                            v-tooltip="$t('legal.nda.generate_pdf')"
                        >
                            <PdfIcon />
                        </ButtonSecondaryIconMd>
                    </div>
                </div>
            </LegalProjectHeader>
        </template>

        <template #sidebar>
            <router-view />
        </template>

        <template #preview>
            <LegalProjectPreview />
        </template>

        <template #footer>
            <LegalProjectFooter :actions="actions" @publish="handlePublish" />
        </template>
    </LayoutPage>
    <!-- Onboarding -->
    <LegalCreator v-if="isCreatorShown" />
    <!-- First steps Video -->
    <cHelpVideosAfterOnboarding v-else />
    <!-- Modal for changing SLUG -->
    <cModalUrl
        v-model:open="showUrlModal"
        v-model:slug="slug"
        :links="legalLinks"
        @updateUuid="changeUuid({ tool: 'legal', uuid: footer.uuid, newUuid: slug })"
    />
    <!-- Modal for renaming -->
    <ModalInput
        v-model:open="showRenameModal"
        v-model:text="projectName"
        :modal-data="modalRenameData"
        @cta="handleRenameProject(projectName)"
        @cancel="showRenameModal = false"
    />
    <!-- Modal for deleting -->
    <ModalConfirm
        v-model="showDeleteModal"
        :modal-data="modalDeleteData"
        @cta="deleteProject"
        @cancel="showDeleteModal = false"
    />
    <cModalShare v-model:open="showModalShare" :links="shareLinks">
        <template #extra>
            <div v-if="ndas.length" class="w-full flex items-center text-left space-x-3">
                <CardIcon>
                    <TextFileIcon />
                </CardIcon>
                <p>NDA</p>
                <FormSelect
                    class="flex-grow"
                    :options="ndaList"
                    :value="selectedNda"
                    @change="selectedNda = $event"
                />
                <div class="space-x-2">
                    <ButtonSecondaryIconMd
                        :disabled="!selectedNda"
                        @click="generateNda"
                        v-tooltip="$t('legal.nda.generate_pdf')"
                    >
                        <PdfIcon />
                    </ButtonSecondaryIconMd>
                </div>
            </div>
        </template>
    </cModalShare>
</template>

<script>
import { markRaw } from "vue";
import { LayoutPage } from "@easy/ui/layouts";
import LegalProjectHeader from "@/apps/legal/project/LegalProjectHeader.vue";
import LegalProjectPreview from "@/apps/legal/project/LegalProjectPreview.vue";
import LegalProjectFooter from "@/apps/legal/project/LegalProjectFooter.vue";
import LegalCreator from "@/apps/legal/project/onboarding/LegalCreator.vue";
import { makeLogger } from "@/shared/consola";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import {
    PencilLineIcon,
    ShareLinkCircleIcon,
    TrashBinIcon,
    LinkShareIcon,
    ShareLinkIcon,
    InvisibleIcon,
    FooterIcon,
    NewFileIcon,
    CodeIcon,
    TextFileIcon,
    PdfIcon,
} from "@easy/ui/icons";
import { CardIcon } from "@easy/ui/cards";
import cModalUrl from "@/shared/components/cModalUrl.vue";
import cModalShare from "@/shared/components/cModalShare.vue";
import { ModalInput, ModalConfirm } from "@easy/ui/modals";
import { FormSelect } from "@easy/ui/forms";
import { EventBus } from "@/shared/projects/event-bus";
import { shareUrlService } from "@/shared/projects/share-url.service";
import { ButtonSecondaryIconMd } from "@easy/ui/buttons";
import { storeToRefs } from "pinia";
import { useLegalCreatorStore } from "@/apps/legal/project/onboarding/legal-creator.store";
import cHelpVideosAfterOnboarding from "@/shared/components/help/cHelpVideosAfterOnboarding.vue";

const logger = makeLogger("LegalProjectPage");

export default {
    components: {
        cHelpVideosAfterOnboarding,
        LayoutPage,
        LegalProjectHeader,
        LegalProjectPreview,
        LegalProjectFooter,
        cModalUrl,
        cModalShare,
        ModalInput,
        ModalConfirm,
        CardIcon,
        TextFileIcon,
        PdfIcon,
        ButtonSecondaryIconMd,
        FormSelect,
        LegalCreator,
    },
    mounted() {
        this.$watch(() => this.$route.params.projectUuid, this.handleProjectChange, {
            immediate: true,
        });
        this.$watch(
            () => this.footer.name,
            (footerName) => {
                this.projectName = footerName || "";
                document.title = `Easylegal | ${footerName}`;
                if (this.projectName) {
                    this.autoOpenCreator(this.footer);
                }
            },
            { immediate: true }
        );
        // FIXME: walk around for now when (publish)
        EventBus.$on("askForChanges", (resolve) => {
            resolve(false);
        });
    },
    setup() {
        const legalCreatorStore = useLegalCreatorStore();
        const { isCreatorShown } = storeToRefs(legalCreatorStore);
        const { autoOpenCreator } = legalCreatorStore;
        return { isCreatorShown, autoOpenCreator };
    },
    data() {
        return {
            showModalShare: false,
            showUrlModal: false,
            showRenameModal: false,
            showDeleteModal: false,
            showShareModel: false,
            slug: this.$route.params.projectUuid, //FIXME: footerUuid, should be legalUuid
            projectName: "",
            selectedNda: "",
            waitForIframeAndGeneratePdf: false,
        };
    },
    watch: {
        iframeLoaded(value) {
            if (value && this.waitForIframeAndGeneratePdf) {
                setTimeout(() => {
                    this.generatePdf();
                    this.waitForIframeAndGeneratePdf = false;
                }, 300);
            }
        },
    },
    computed: {
        ...mapState(["globalUrl", "previewLang"]),
        ...mapState("legal", ["footer", "legal", "iframeLoaded"]),
        ...mapState("legal/ndas", ["ndas"]),
        ...mapGetters(["isPublished"]),
        ...mapGetters("legal", ["footerIframe", "termsIframe", "policyIframe"]),
        actions() {
            return [
                {
                    label: this.$t("shared.live_preview"),
                    icon: markRaw(LinkShareIcon),
                    handler: () => {
                        window.open(this.legalURL, "_blank");
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("legal.get_footer_code"),
                    icon: markRaw(CodeIcon),
                    handler: () => {
                        this.showCodeFooter();
                    },
                },
                {
                    label: this.$t("shared.share"),
                    icon: markRaw(ShareLinkIcon),
                    handler: () => {
                        this.showModalShare = true;
                        this.getNdas();
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("shared.rename"),
                    icon: markRaw(PencilLineIcon),
                    handler: () => {
                        this.showRenameModal = true;
                    },
                },
                {
                    label: this.$t("shared.change_url.title"),
                    icon: markRaw(ShareLinkCircleIcon),
                    handler: () => {
                        this.showUrlModal = true;
                    },
                },
                this.isPublished(this.footer)
                    ? {
                          label: this.$t("shared.unpublish"),
                          icon: markRaw(InvisibleIcon),
                          handler: () => {
                              this.unpublishFooter();
                              logger.log("unpublish");
                          },
                      }
                    : {},
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    isDanger: true,
                    icon: markRaw(TrashBinIcon),
                    handler: () => {
                        this.showDeleteModal = true;
                    },
                },
            ];
        },
        legalLinks() {
            return [
                {
                    title: this.$t("legal.views.terms"),
                    url: `${this.globalUrl}/terms/<strong>${this.slug}</strong>`,
                },
                {
                    title: this.$t("legal.views.privacy"),
                    url: `${this.globalUrl}/policies/<strong>${this.slug}</strong>`,
                },
                {
                    title: this.$t("legal.views.about"),
                    url: `${this.globalUrl}/about/<strong>${this.slug}</strong>`,
                },
            ];
        },
        legalURL() {
            const slug = this.footer.uuid;
            return shareUrlService.makeUrlWithLang("footers2", slug, this.previewLang);
        },
        modalRenameData() {
            return {
                title: this.$t("shared.rename"),
                inputLabel: this.$t("shared.project_name"),
                ctaLabel: this.$t("shared.save"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        modalDeleteData() {
            return {
                title: this.$t("shared.delete_project"),
                description: this.$t("shared.delete_project_info"),
                ctaLabel: this.$t("shared.delete"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        shareLinks() {
            const footerSlug = this.footer.uuid;
            const footerURL = shareUrlService.makeUrlWithLang(
                "footers",
                footerSlug,
                this.previewLang
            );
            const legalSlug = this.legal.uuid;
            const termsURL = shareUrlService.makeUrlWithLang("terms", legalSlug, this.previewLang);
            const privacyURL = shareUrlService.makeUrlWithLang(
                "policies",
                legalSlug,
                this.previewLang
            );
            const aboutURL = shareUrlService.makeUrlWithLang("about", legalSlug, this.previewLang);
            return [
                {
                    value: "footer",
                    label: this.$t("legal.views.footer"),
                    url: footerURL,
                    icon: markRaw(FooterIcon),
                    code: this.footerIframe,
                },
                {
                    value: "terms",
                    label: this.$t("legal.views.terms"),
                    url: termsURL,
                    icon: markRaw(NewFileIcon),
                    code: this.termsIframe,
                },
                {
                    value: "privacy",
                    label: this.$t("legal.views.privacy"),
                    url: markRaw(privacyURL),
                    icon: NewFileIcon,
                    code: this.policyIframe,
                },
                {
                    value: "about",
                    label: this.$t("legal.views.about"),
                    url: aboutURL,
                    icon: markRaw(NewFileIcon),
                    code: "",
                },
            ];
        },
        ndaList() {
            return this.ndas.map((nda) => ({ label: nda.name, value: nda.id }));
        },
    },
    methods: {
        ...mapActions("legal", ["publishProject", "unpublishFooter", "showCodeFooter"]),
        ...mapActions("legal/projects", ["loadProject"]),
        ...mapActions("project", ["changeUuid", "removeProject", "renameProject"]),
        ...mapActions("legal/ndas", ["getNdas", "generatePdf", "setCurrentNda"]),
        ...mapMutations("legal", ["setIframeLoaded"]),
        handleProjectChange(uuid, oldUuid) {
            if (uuid && uuid !== oldUuid) {
                this.loadProject(uuid);
            }
        },
        async handleRenameProject(newName) {
            // FIXME There is problem with a SNAPSHOT.
            // When you rename project snapshot updates and overrides current data :(
            const error = await this.renameProject({
                tool: "legal",
                project: this.footer,
                newName,
            });
            if (!error) {
                this.showRenameModal = false;
            }
        },
        async deleteProject() {
            const error = await this.removeProject({ tool: "legal", uuid: this.footer.uuid });
            if (!error) {
                this.showDeleteModal = false;
            }
        },
        handlePublish() {
            this.publishProject();
        },
        async generateNda() {
            const nda = this.ndas.find((nda) => nda.id === this.selectedNda);
            this.setIframeLoaded(false);
            this.setCurrentNda(nda);
            if (this.iframeLoaded) {
                this.generatePdf();
            } else {
                this.waitForIframeAndGeneratePdf = true;
            }
        },
    },
};
</script>
